<template>
  <div class="bzs-root" :class="'is-' + variant" data-test="ask-bizminer-share" @keydown.esc="closeAll">
    <!-- Panel header: a conversation menu (the kebab) whose item opens the confirm step. -->
    <template v-if="variant === 'menu'">
      <button
        type="button"
        class="bzs-kebab"
        aria-haspopup="menu"
        :aria-expanded="menuOpen ? 'true' : 'false'"
        aria-label="Conversation menu"
        title="Conversation menu"
        :disabled="disabled || busy"
        data-test="ask-bizminer-menu"
        @click.stop="toggleMenu"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true" fill="currentColor"><path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"></path></svg>
      </button>
      <div v-if="menuOpen" class="bzs-menu" role="menu" data-test="ask-bizminer-menu-list">
        <button type="button" class="bzs-item" role="menuitem" data-test="ask-bizminer-share-item" @click.stop="ask(shared ? 'unshare' : 'share')">
          {{ shared ? 'Stop sharing with support' : 'Share with support' }}
        </button>
      </div>
    </template>
    <!-- /chat page header: the action sits beside Delete. -->
    <button
      v-else
      type="button"
      class="bzs-btn"
      :disabled="disabled || busy"
      data-test="chat-page-share"
      @click.stop="ask(shared ? 'unshare' : 'share')"
    >
      <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" fill="currentColor"><path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5s-3 1.34-3 3 1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"></path></svg>
      <span class="bzs-btn-label">{{ shared ? 'Stop sharing' : 'Share with support' }}</span>
    </button>

    <!-- The confirm step. Sharing lets staff read the conversation, so nothing is sent before Share is pressed. -->
    <div
      v-if="mode"
      ref="dialog"
      class="bzs-dialog"
      role="alertdialog"
      aria-labelledby="bzs-title"
      aria-describedby="bzs-text"
      data-test="ask-bizminer-share-dialog"
      @click.stop
    >
      <template v-if="mode === 'done'">
        <div id="bzs-title" class="bzs-title">Shared with support</div>
        <p id="bzs-text" class="bzs-text" data-test="ask-bizminer-share-done">{{ doneNote }}</p>
        <div class="bzs-actions">
          <button ref="focus" type="button" class="bzs-primary" @click="closeAll">Done</button>
        </div>
      </template>
      <template v-else>
        <div id="bzs-title" class="bzs-title">{{ dialog.title }}</div>
        <p id="bzs-text" class="bzs-text" data-test="ask-bizminer-share-note">{{ dialog.text }}</p>
        <p v-if="error" class="bzs-error" role="alert" data-test="ask-bizminer-share-error">{{ error }}</p>
        <div class="bzs-actions">
          <button type="button" class="bzs-primary" :disabled="busy" data-test="ask-bizminer-share-confirm" @click="confirm">{{ busy ? dialog.busyLabel : error ? 'Try again' : dialog.confirmLabel }}</button>
          <button ref="focus" type="button" class="bzs-ghost" :disabled="busy" data-test="ask-bizminer-share-cancel" @click="closeAll">Cancel</button>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
// Share with support / Stop sharing (CHAT-08, PFD-220). One control for both surfaces: the panel's
// header menu (`variant="menu"`) and the /chat page header (`variant="button"`). The parent owns what
// "shared" means for the open conversation; this component runs the confirm step and the two calls and
// reports back. Wording and error mapping are in chatCore (shareDialog, shareFailure, normalizeShare).
import { shareDialog, normalizeShare, shareFailure } from './chatCore';
import { shareConversation, unshareConversation, withReauth } from './chatApi';

export default {
  name: 'chat-share-menu',
  props: {
    conversationId: { type: String, default: null },
    shared: { type: Boolean, default: false },
    // The service's note, once this session has seen it (a share response or a shared conversation).
    shareNote: { type: String, default: '' },
    disabled: { type: Boolean, default: false },
    variant: { type: String, default: 'button' }, // 'menu' | 'button'
  },
  data() {
    return {
      menuOpen: false,
      mode: null, // null | 'share' | 'unshare' | 'done'
      busy: false,
      error: '',
      doneNote: '',
    };
  },
  computed: {
    dialog() {
      return shareDialog(this.mode === 'unshare' ? 'unshare' : 'share', this.shareNote);
    },
  },
  watch: {
    // Another conversation: whatever was open here belonged to the old one.
    conversationId() {
      this.closeAll();
    },
    disabled(v) {
      if (v && !this.busy) this.closeAll();
    },
  },
  mounted() {
    document.addEventListener('click', this.onDocClick);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.onDocClick);
    this.gone = true;
  },
  methods: {
    onDocClick() {
      if (this.menuOpen) this.menuOpen = false;
    },
    toggleMenu() {
      this.menuOpen = !this.menuOpen;
    },
    closeAll() {
      if (this.busy) return;
      this.menuOpen = false;
      this.mode = null;
      this.error = '';
      this.doneNote = '';
    },
    ask(mode) {
      if (!this.conversationId || this.disabled || this.busy) return;
      this.menuOpen = false;
      this.error = '';
      this.mode = mode;
      this.$nextTick(() => this.$refs.focus && this.$refs.focus.focus());
    },
    async confirm() {
      const id = this.conversationId;
      const action = this.mode === 'unshare' ? 'unshare' : 'share';
      if (!id || this.busy || (this.mode !== 'share' && this.mode !== 'unshare')) return;
      this.busy = true;
      this.error = '';
      try {
        if (action === 'share') {
          const s = normalizeShare(await withReauth(() => shareConversation(id)));
          if (this.gone) return;
          if (!s) throw Object.assign(new Error('bad_share_response'), { status: 502, code: 'bad_response' });
          this.$emit('shared', { id, sharedAt: s.sharedAt, shareNote: s.shareNote });
          // Show the service's own note: what sharing means, as it said it.
          this.doneNote = s.shareNote || this.dialog.text;
          this.mode = this.conversationId === id ? 'done' : null;
        } else {
          await withReauth(() => unshareConversation(id));
          if (this.gone) return;
          this.$emit('unshared', { id });
          this.mode = null;
        }
      } catch (e) {
        if (this.gone) return;
        const f = shareFailure(e, action);
        if (f.kind === 'gone') {
          this.mode = null;
          this.$emit('missing', id);
        } else if (f.kind === 'subscription' || f.kind === 'denied') {
          this.mode = null;
          this.$emit('access', e);
        } else {
          this.error = f.text;
        }
      } finally {
        this.busy = false;
      }
    },
  },
};
</script>

<style lang="scss">
$bzs-navy: #003666;
$bzs-cyan: #0cbcf2;

.bzs-root {
  position: relative;
  display: inline-flex;
  font-family: Roboto, Arial, sans-serif;
  text-align: left;
  button { font: inherit; cursor: pointer; }
  button:disabled { cursor: default; }
  p { margin: 0; }
}
// Header kebab: sits on the panel's navy bar like its other icon buttons.
.bzs-kebab {
  width: 36px; height: 36px; border: 0; border-radius: 18px; background: transparent; color: #fff;
  display: inline-flex; align-items: center; justify-content: center;
  &:hover:not(:disabled) { background: rgba(255, 255, 255, 0.14); }
  &:disabled { opacity: 0.45; }
  &:focus-visible { outline: 2px solid #fff; outline-offset: -2px; }
}
.bzs-menu {
  position: absolute; right: 0; top: calc(100% + 4px); z-index: 30; min-width: 220px; padding: 4px 0;
  background: #fff; color: #22303f; border: 1px solid #d7dde5; border-radius: 8px; box-shadow: 0 6px 18px rgba(0, 30, 60, 0.2);
}
.bzs-item {
  display: block; width: 100%; padding: 0 16px; min-height: 40px; border: 0; background: transparent; text-align: left; font-size: 13.5px; color: #22303f;
  &:hover { background: #f1f5f9; }
  &:focus-visible { outline: 2px solid $bzs-cyan; outline-offset: -2px; }
}
// /chat page: an outline button like Delete.
.bzs-btn {
  display: inline-flex; align-items: center; gap: 6px; min-height: 36px; padding: 0 12px; border-radius: 6px; border: 1px solid #c9d1db; background: #fff;
  color: #3c4656; font-size: 13px;
  &:hover:not(:disabled) { background: #f1f3f6; }
  &:disabled { opacity: 0.55; }
  &:focus-visible { outline: 2px solid $bzs-cyan; outline-offset: 2px; }
}
.bzs-dialog {
  position: absolute; right: 0; top: calc(100% + 6px); z-index: 30; box-sizing: border-box; width: 300px; max-width: calc(100vw - 32px);
  padding: 14px 14px 12px; background: #fff; color: #22303f; border: 1px solid #b8cde0; border-radius: 10px; box-shadow: 0 8px 24px rgba(0, 30, 60, 0.24);
  display: flex; flex-direction: column; gap: 8px;
}
.bzs-title { font-family: Inter, Roboto, sans-serif; font-size: 14px; font-weight: 700; color: $bzs-navy; }
.bzs-text { font-size: 13px; line-height: 1.45; color: #3c4656; }
.bzs-error { font-size: 12.5px; line-height: 1.4; color: #a3261c; }
.bzs-actions { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 2px; }
.bzs-primary, .bzs-ghost {
  min-height: 36px; padding: 0 14px; border-radius: 6px; border: 1px solid transparent; font-size: 13px; font-weight: 500;
  &:disabled { opacity: 0.55; }
  &:focus-visible { outline: 2px solid $bzs-cyan; outline-offset: 2px; }
}
.bzs-primary { background: $bzs-navy; color: #fff; &:hover:not(:disabled) { background: #002a50; } }
.bzs-ghost { background: #fff; border-color: #c9d1db; color: #3c4656; &:hover:not(:disabled) { background: #f1f3f6; } }

@media (max-width: 600px) {
  .bzs-btn { min-height: 44px; min-width: 44px; padding: 0; justify-content: center; }
  .bzs-btn .bzs-btn-label { position: absolute; left: -9999px; }
  .bzs-kebab { width: 44px; height: 44px; border-radius: 22px; }
  .bzs-primary, .bzs-ghost { min-height: 44px; }
  .bzs-item { min-height: 44px; }
}
</style>
